import React, { useEffect, useMemo, useState } from 'react';
import StudentChipList from './StudentChipList';

type DomainScore = { section: string; percent: number };
type StudentBreakdown = {
    studentId: number;
    firstName: string;
    lastName: string;
    domains: { [domain: string]: DomainScore };
};

type FocusAreasProps = {
    latestTest?: {
        testName?: string;
        testDate?: string;
        studentDomainBreakdown?: StudentBreakdown[] | null;
    };
};

const THRESHOLDS = [50, 60, 70];

// Preferred display order for the section filter (covers SAT/PSAT/ACT/SHSAT/TSIA2).
const SECTION_ORDER = ['R/W', 'ELA', 'ELAR', 'English', 'Reading', 'Math', 'Science'];

const SECTION_PILL: { [s: string]: string } = {
    'R/W': 'cdr__rw',
    'ELA': 'cdr__rw',
    'ELAR': 'cdr__rw',
    'English': 'cdr__english',
    'Reading': 'cdr__reading',
    'Math': 'cdr__math',
    'Science': 'cdr__science',
};

// Fixed severity bands (how far below passing a student is)
const sevOf = (score: number) => (score < 40 ? 'crit' : score < 50 ? 'high' : 'mod');
const pillCls = (section: string) => SECTION_PILL[section] || 'cdr__mixed';

// Class-avg bar tint (mirrors the Completed bar in Assignment Tracking): a low
// class average means the domain is weaker, so it reads redder.
const avgBarCls = (pct: number) => (pct >= 70 ? 'success' : pct >= 50 ? 'warning' : 'danger');

function FocusAreas({ latestTest }: FocusAreasProps) {
    const [threshold, setThreshold] = useState(60);
    const [sectionFilter, setSectionFilter] = useState('All');
    const [expanded, setExpanded] = useState<string | null>(null);

    const students = latestTest?.studentDomainBreakdown || [];

    // All domain groups for this test (independent of the section filter).
    const baseGroups = useMemo(() => {
        const domainMeta: { [domain: string]: string } = {};
        students.forEach(s => {
            Object.entries(s.domains || {}).forEach(([domain, info]) => {
                if (!domainMeta[domain]) domainMeta[domain] = info.section;
            });
        });

        return Object.entries(domainMeta)
            .map(([domain, section]) => {
                const scored = students
                    .filter(s => s.domains?.[domain] != null)
                    .map(s => ({
                        id: s.studentId,
                        name: `${s.firstName} ${s.lastName}`.trim(),
                        firstName: s.firstName,
                        lastName: s.lastName,
                        score: s.domains[domain].percent,
                    }));
                const weak = scored
                    .filter(s => s.score < threshold)
                    .sort((a, b) => a.score - b.score);
                const avg = scored.length
                    ? Math.round(scored.reduce((sum, s) => sum + s.score, 0) / scored.length)
                    : 0;
                return { name: domain, section, weak, avg };
            })
            // Keep domains in their natural section order (e.g. ELA before Math,
            // R/W before Math) rather than ordering by how many students need
            // support. Stable sort preserves each section's domain order.
            .sort((a, b) => {
                const ia = SECTION_ORDER.indexOf(a.section);
                const ib = SECTION_ORDER.indexOf(b.section);
                return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
            });
    }, [students, threshold]);

    // Section filter options, derived from the sections this test type actually has.
    const sections = useMemo(() => {
        const present = Array.from(new Set(baseGroups.map(g => g.section)));
        return present.sort((a, b) => {
            const ia = SECTION_ORDER.indexOf(a);
            const ib = SECTION_ORDER.indexOf(b);
            return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        });
    }, [baseGroups]);

    // Reset the section filter when the available sections change (e.g. test type switch).
    useEffect(() => { setSectionFilter('All'); }, [sections.join('|')]);

    const groups = useMemo(
        () => (sectionFilter === 'All' ? baseGroups : baseGroups.filter(g => g.section === sectionFilter)),
        [baseGroups, sectionFilter]
    );

    const totalFlagged = useMemo(
        () => new Set(baseGroups.flatMap(g => g.weak.map(s => s.id))).size,
        [baseGroups]
    );

    if (!students.length) {
        return (
            <div className="cdr__tab-content">
                <div className="cdr__empty">
                    No graded results yet for this test type — focus areas will appear once
                    students complete the latest test.
                </div>
            </div>
        );
    }

    return (
        <div className="cdr__tab-content">
            <div className="cdr__focus-bar">
                <div className="cdr__focus-bar-left">
                    <span className="cdr__focus-bar-label">Flag students scoring below</span>
                    <div className="cdr__seg">
                        {THRESHOLDS.map(t => (
                            <button
                                key={t}
                                className={threshold === t ? 'active' : ''}
                                onClick={() => setThreshold(t)}
                            >
                                {t}%
                            </button>
                        ))}
                    </div>
                    <div className="cdr__chip-group">
                        {['All', ...sections].map(s => (
                            <button
                                key={s}
                                className={sectionFilter === s ? 'active' : ''}
                                onClick={() => setSectionFilter(s)}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="cdr__focus-bar-note">
                    <strong>{totalFlagged}</strong> of {students.length} students flagged in at
                    least one domain
                </div>
            </div>

            <div className="cdr__card cdr__card--flush">
                <table className="cdr__tbl">
                    <thead>
                        <tr>
                            <th style={{ width: '42%' }}>Domain</th>
                            <th>Section</th>
                            <th>Needs support</th>
                            <th style={{ width: 200 }}>Class avg</th>
                            <th style={{ width: 60 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.length === 0 && (
                            <tr>
                                <td colSpan={5} className="cdr__tbl-empty">
                                    No domains for this section.
                                </td>
                            </tr>
                        )}
                        {groups.map(g => {
                            const empty = g.weak.length === 0;
                            const isOpen = expanded === g.name;
                            return (
                                <React.Fragment key={g.name}>
                                    <tr
                                        className={`cdr__row${isOpen ? ' expanded' : ''}`}
                                        onClick={() => setExpanded(isOpen ? null : g.name)}
                                    >
                                        <td className="cdr__name-cell">
                                            <div>{g.name}</div>
                                        </td>
                                        <td>
                                            <span className={`cdr__section-pill ${pillCls(g.section)}`}>
                                                {g.section}
                                            </span>
                                        </td>
                                        <td>
                                            {empty ? (
                                                <span className="cdr__focus-allclear">All clear</span>
                                            ) : (
                                                <span className="cdr__focus-count">
                                                    <strong>{g.weak.length}</strong> student{g.weak.length === 1 ? '' : 's'}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="cdr__comp">
                                                <div className={`cdr__comp-bar ${avgBarCls(g.avg)}`}>
                                                    <span style={{ width: `${g.avg}%` }}></span>
                                                </div>
                                                <span className="cdr__comp-text"><strong>{g.avg}%</strong></span>
                                            </div>
                                        </td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <button
                                                className={`cdr__expand-btn${isOpen ? ' open' : ''}`}
                                                onClick={() => setExpanded(isOpen ? null : g.name)}
                                                aria-label="Show students needing support"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="m6 9 6 6 6-6" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="cdr__detail">
                                            <td colSpan={5}>
                                                <div className="cdr__detail-panel">
                                                    {empty ? (
                                                        <div className="cdr__detail-note cdr__detail-note--done">
                                                            Every student is above {threshold}% in this domain.
                                                        </div>
                                                    ) : (
                                                        <StudentChipList
                                                            header={<><strong>{g.weak.length}</strong> student{g.weak.length === 1 ? '' : 's'} need support · class avg {g.avg}%</>}
                                                            students={g.weak}
                                                            quad
                                                            showScore
                                                            sevOf={sevOf}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default FocusAreas;
